import { isLikelyImageAttachment } from '@utils/chatAttachmentKind.util';

/**
 * How to show an in-app preview when the user taps a file attachment card.
 * @returns {'image' | 'video' | 'pdf' | 'text' | 'none'}
 */
export function getChatFileInlinePreviewMode({ mimeType, name, url } = {}) {
    const m = String(mimeType || '').toLowerCase();
    const bundle = `${String(name || '')} ${String(url || '')}`.toLowerCase();
    if (isLikelyImageAttachment({ name, url, mimeType: m, filePath: '' })) return 'image';
    // Voice notes use .webm (audio container); treat as non-video so preview/gallery stays audio/file — not video.
    if (m.startsWith('audio/') || /^voice\./i.test(String(name || '').trim())) return 'none';
    if (m.startsWith('video/') || /\.(webm|mp4|mov|mkv|avi|m4v|ogv)(\?|#|$)/i.test(bundle)) return 'video';
    if (m === 'application/pdf' || /\.pdf(\?|#|$)/i.test(bundle)) return 'pdf';
    if (
        m.startsWith('text/') ||
        /\.(txt|csv|md|json|log|xml|html?|css|js|mjs|c|cpp|h|java|py|rb|php|yml|yaml|sh)(\?|#|$)/i.test(bundle)
    ) {
        return 'text';
    }
    return 'none';
}

function pickPreviewFromPreviewImagesValue(pi) {
    if (typeof pi === 'string') {
        const t = pi.trim();
        return t || null;
    }
    if (Array.isArray(pi)) {
        for (const item of pi) {
            if (typeof item === 'string') {
                const t = item.trim();
                if (t) return t;
            }
            if (item && typeof item === 'object') {
                const u = item.url ?? item.href ?? item.src;
                if (typeof u === 'string' && u.trim()) return u.trim();
            }
        }
    }
    return null;
}

/**
 * First preview image URL from an attachment (`previewImages`) or message-level array by index.
 * Not resolved — pass through {@link resolveChatMediaUrl}.
 */
export function getAttachmentPreviewImageRaw(attachment, attachmentIndex, messagePreviewImages) {
    if (attachment && typeof attachment === 'object') {
        const own =
            pickPreviewFromPreviewImagesValue(attachment.previewImages) ??
            pickPreviewFromPreviewImagesValue(attachment.preview_images);
        if (own) return own;
    }
    if (Array.isArray(messagePreviewImages) && typeof attachmentIndex === 'number' && attachmentIndex >= 0) {
        const item = messagePreviewImages[attachmentIndex];
        if (typeof item === 'string') {
            const t = item.trim();
            if (t) return t;
        }
        if (item && typeof item === 'object') {
            const u = item.url ?? item.href ?? item.src ?? item.previewUrl;
            if (typeof u === 'string' && u.trim()) return u.trim();
            const nested = pickPreviewFromPreviewImagesValue(item.previewImages ?? item.preview_images);
            if (nested) return nested;
        }
    }
    return null;
}
